/**
 * src/hooks/useCurrentUser.ts
 *
 * Single source of truth for the /api/auth/me query.
 * Both the _authenticated route guard (beforeLoad via ensureQueryData)
 * and the AppShell component (via useQuery) use the same queryKey and
 * config so TanStack Query serves AppShell from cache — no duplicate
 * network request.
 */
import { queryOptions, useQuery } from "@tanstack/react-query";
import { getMe } from "@/integrations/api/auth";
import type { MeResult } from "@/integrations/api/auth";

export const currentUserQueryOptions = () =>
  queryOptions<MeResult>({
    queryKey: ["current-user"],
    queryFn: getMe,
    staleTime: 30 * 1000,         // 30 seconds — short enough to reflect permission changes quickly
    gcTime: Infinity,             // keep in cache until explicitly cleared (e.g. on logout)
    refetchOnWindowFocus: true,   // re-fetch when user switches back to tab so permissions are fresh
    refetchOnReconnect: true,
    retry: false,
  });

/** React hook for components that need current user data */
export function useCurrentUser() {
  return useQuery(currentUserQueryOptions());
}
